"use client";

import Link from "@casino/client/components/Link";
import { useState } from "react";
import convertHexToRGBA from "./convertHexToRGBA";
import ThemeContent, { type ThemeGame } from "./ThemeContent";

type Props = {
  handle: string;
  background?: string;
  title: string;
  games: ThemeGame[];
  isMobile: boolean;
};

const ThemeCard: FC<Props> = ({
  handle,
  background,
  title,
  games,
  isMobile,
}) => {
  const color = parseBackgroundColor(background);
  const bgColor = convertHexToRGBA(color, 0.08);
  const bgColorHover = convertHexToRGBA(color, 0.16);
  const [cardBackground, setCardBackground] = useState(bgColor);

  const handleMouseOver = () => {
    if (!isMobile && bgColorHover) {
      setCardBackground(bgColorHover);
    }
  };

  const handleMouseLeave = () => {
    setCardBackground(bgColor);
  };

  // Nested anchors are invalid HTML (the game links live inside the card),
  // so the theme link is an absolutely-positioned sibling underneath them.
  return (
    <div
      className={
        isMobile
          ? "relative rounded-xl pb-4 pl-5 pr-4 pt-8"
          : "relative rounded-xl py-1 pl-[1.375rem] pr-1"
      }
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      style={{ backgroundColor: cardBackground }}
      data-testid={isMobile ? "mobile-theme-card" : "desktop-theme-card"}
    >
      <Link
        href={`/themes/${handle}`}
        data-testid="theme-link"
        aria-label={title}
        className="absolute inset-0 z-1 rounded-xl"
      />
      <div className="min-h-[184px] min-w-[280px] md:min-h-[280px] md:min-w-[440px]">
        <ThemeContent
          title={title}
          color={color}
          games={games}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

const parseBackgroundColor = (background?: string) => {
  try {
    const backgroundObj = JSON.parse(background || "{}");
    return backgroundObj["background-color"] as string;
  } catch {
    return "";
  }
};

export default ThemeCard;
